import { RouteComponentProps } from 'react-router';
import { frontloadConnect } from 'react-frontload';
import { connect } from 'react-redux';
import {
  generatePlpSchema as _generatePlpSchema,
  generateBreadcrumbSchema as _generateBreadcrumbSchema,
  generateCollectionPageSchema as _generateCollectionPageSchema,
  JSONLdData,
} from '@nykaa/jsonld-shared/store/action';
import { CollectionPageDataType } from '@nykaa/jsonld-shared/types';
import redirectIncorrectSlug from '@nykaa/seo/utils/redirectIncorrectSlug';
import { getAbsoluteUrl } from '@nykaa/utils/network/urls';
import {
  fetchCategoryListing as _fetchCategoryListing,
  setActiveFilters as _setActiveFilters,
  fetchCategoryListingWidgets as _fetchCategoryListingWidgets,
  setDataLayer as _setDataLayer,
} from '@nykaa/listing-shared/category/store/action';
import {
  FetchCategoryListingParams,
  MatchProps,
} from '@nykaa/listing-shared/category/store/types';
import {
  getApiRequestObject,
  createActiveFilterObjectFromURL,
} from '@nykaa/listing-shared/utils/filter';
import { getDnPageTypeFromWidgetData } from '@nykaa/listing-shared/search/store/helpers';
import { ActiveFilter } from '@nykaa/listing-shared/type/filter';
import View from 'layouts/CategoryListing';
import { State } from 'store/types';
import { deviceTypes } from 'shared/constants';
import {
  HIDDEN_UNDERSCORE,
  IS_FILTERED_URL,
} from '@nykaa/listing-shared/constants';
import { getPRListAndPRCount } from '@nykaa/listing-shared/utils/product';
import { PagePrefixes } from 'constants/pagePrefixes';
import { fetchBreadCrumbs as _fetchBreadCrumbs } from 'store/app/actions';
import { BreadCrumb } from 'store/app/types';
import { logger } from '@nykaa/logger';
import { fetchAggregatedFooterData as _fetchAggregatedFooterData } from '@nykaa/listing-shared/category/store/action';
import { DWEB } from 'constants/devices';
import getDynamicAlgoParams from 'helpers/dynamicAlgoParams';
import { QUERY_PARAM_EXP_DWEB } from 'constants/experiments';
import appEventTypes from 'shared/store/app/actionTypes';
import PageTypes from 'constants/pageTypes';
import { isAggregatorFooterEnabled } from 'hooks/category/useAggregatorFooter';

const mapStateToProps = (state: State) => {
  const { auth, remoteConfig, categoryListing } = state;
  const categoryListingState = categoryListing as any;
  const remoteConfigs = remoteConfig.configs as any;

  return {
    isPro: auth.user.isPro,
    groupId: auth.user.groupId,
    isRedirectEligible: remoteConfig.switches?.isSlugRedirectEnabled,
    redirectBlockList: remoteConfig.configs?.plpRedirectTargetBlockList,
    redirectionParamBlackList: remoteConfig.configs?.redirectionParamBlackList,
    listingPriceRevealEnabled:
      remoteConfig.configs?.listingPriceReveal.isActive,
    fetchFooterDataOnClient: categoryListingState.fetchFooterDataOnClient,
    isAggregatorFooterEnabled: isAggregatorFooterEnabled(state),
    aggregatorBreadcrumbFooterTimeout:
      remoteConfigs?.aggregatorBreadcrumbFooterTimeout,
    state,
    priveBand: auth.user?.priveBand || null,
  };
};

const mapDispatchToProps = {
  fetchCategoryListing: _fetchCategoryListing,
  setActiveFilters: _setActiveFilters,
  fetchCategoryListingWidgets: _fetchCategoryListingWidgets,
  setDataLayer: _setDataLayer,
  fetchAggregatedFooterData: _fetchAggregatedFooterData,
  setPageType: (payload: string) => ({
    type: appEventTypes.APP_REDUCER_SET_PAGE,
    payload,
  }),
  generatePlpSchema: _generatePlpSchema,
  fetchBreadCrumbs: _fetchBreadCrumbs,
  generateBreadcrumbSchema: _generateBreadcrumbSchema,
  generateCollectionPageSchema: _generateCollectionPageSchema,
};

interface FetchDataProps extends RouteComponentProps<MatchProps> {
  isPro: boolean;
  groupId: string;
  fetchCategoryListing: (params: FetchCategoryListingParams) => Promise<void>;
  setActiveFilters: (params: ActiveFilter | string | number) => Promise<void>;
  fetchCategoryListingWidgets: (
    params: any,
    store?: string,
    pagePrefix?: string
  ) => Promise<void>;
  setDataLayer: (params: any) => Promise<void>;
  fetchAggregatedFooterData: (
    params: { cat_id: string; domain: string },
    timeout?: number
  ) => Promise<any>;
  setPageType: (payload: string) => void;
  generatePlpSchema: (data: JSONLdData) => void;
  fetchBreadCrumbs: (id: string) => Promise<void>;
  generateBreadcrumbSchema: (data: JSONLdData) => void;
  generateCollectionPageSchema: (data: CollectionPageDataType) => void;
  staticContext: CustomStaticContext;
  isRedirectEligible: boolean;
  redirectBlockList: string[];
  redirectionParamBlackList: string[];
  listingPriceRevealEnabled: boolean;
  isAggregatorFooterEnabled: boolean;
  aggregatorBreadcrumbFooterTimeout?: number;
  fetchFooterDataOnClient: boolean;
  state: State;
  priveBand: string;
}
interface WidgetsParams {
  page_data: string;
  page_type: string;
  page_section: string;
  [key: string]: string;
}
const fetchData = async ({
  fetchCategoryListing,
  setActiveFilters,
  match,
  location,
  isPro,
  fetchCategoryListingWidgets,
  setDataLayer,
  fetchAggregatedFooterData,
  setPageType,
  groupId,
  generatePlpSchema,
  fetchBreadCrumbs,
  generateBreadcrumbSchema,
  generateCollectionPageSchema,
  staticContext,
  isRedirectEligible,
  redirectBlockList,
  redirectionParamBlackList,
  listingPriceRevealEnabled,
  isAggregatorFooterEnabled,
  aggregatorBreadcrumbFooterTimeout,
  state,
  priveBand,
}: FetchDataProps) => {
  const { pathname, search } = location;
  const id = match?.params?.id || '';
  setPageType(PageTypes.LISTING);
  const widgetsParams: WidgetsParams = {
    page_data: id,
    page_type: 'desktop-category-listing',
    page_section: '*',
    dn_platform: DWEB,
  };
  const params: { [key: string]: string } = getApiRequestObject(search);
  const dynamicParams = getDynamicAlgoParams(state, QUERY_PARAM_EXP_DWEB);
  for (const property in params) {
    const isOptionalParam =
      property?.startsWith(HIDDEN_UNDERSCORE) || property === IS_FILTERED_URL;
    if (isOptionalParam) {
      widgetsParams[property] = params[property];
    }
  }
  let widgetFetch;
  /* eslint-disable no-prototype-builtins */
  if (params.hasOwnProperty('plpwidgets') && params['plpwidgets'] === 'false') {
    widgetFetch = Promise.resolve();
  } else {
    widgetFetch = fetchCategoryListingWidgets(
      widgetsParams,
      __PLATFORM__,
      PagePrefixes.PRODUCT_LISTING_PAGE
    );
  }
  // TODO: MSR-1499 - extract filter and sort from URL
  // TODO: Handle Pro user on server side
  // return fetchCategoryListing({ categoryId: id });
  const parameters = {
    pageNumber: 1,
    categoryId: id,
    isPro,
    customer_group_id: groupId,
    isFirstFetch: true,
    platform: deviceTypes.D_SITE,
    listingPriceRevealEnabled,
    ...params,
    ...dynamicParams,
    priveBand,
  };
  const categoryFetch = fetchCategoryListing(parameters);
  // On SSR: use the aggregated footer API (with short timeout) when the experiment is enabled.
  // On client-side navigation: always use the dedicated breadcrumb API because the aggregated
  // footer API is not required on the client and may fail, which would leave breadcrumbs stale.
  const footerDataFetch =
    isAggregatorFooterEnabled && __SERVER__
      ? fetchAggregatedFooterData(
          { cat_id: id, domain: __PLATFORM__ },
          aggregatorBreadcrumbFooterTimeout
        )
      : fetchBreadCrumbs(id);
  await Promise.allSettled([categoryFetch, widgetFetch, footerDataFetch]).then(
    (res: any) => {
      try {
        const [
          { value: categoryFetchResponse = {} } = {},
          { value: widgetFetchResponse = [] } = {},
          { value: footerDataResponse = {} } = {},
        ] = res || {};
        const breadcrumbs =
          isAggregatorFooterEnabled && __SERVER__
            ? footerDataResponse?.breadcrumbs || []
            : footerDataResponse || [];

        if (breadcrumbs?.length) {
          generateBreadcrumbSchema(breadcrumbs as BreadCrumb[]);
        }
        const dnHybridPageType =
          getDnPageTypeFromWidgetData(widgetFetchResponse);
        const activeFilters = createActiveFilterObjectFromURL(
          categoryFetchResponse?.filters,
          search
        );
        setActiveFilters(activeFilters);
        if (categoryFetchResponse?.products?.length) {
          generatePlpSchema(categoryFetchResponse.products);
        }

        const { canonical, categoryName, canonicalUrl, metaDescription } =
          categoryFetchResponse || {};

        if (isRedirectEligible) {
          redirectIncorrectSlug({
            source: pathname,
            target: canonical,
            context: staticContext,
            queryString: location?.search || '',
            blockList: redirectBlockList,
            splitOn: '/c/',
            queryBlackList: redirectionParamBlackList,
          });
        }

        // Generate CollectionPage schema for category listing
        if (categoryName && (canonicalUrl || canonical)) {
          const collectionPageData: CollectionPageDataType = {
            name: categoryName,
            url: canonicalUrl || getAbsoluteUrl(canonical),
            ...(metaDescription && {
              description: metaDescription,
            }),
          };
          generateCollectionPageSchema(collectionPageData);
        }

        const listingType = categoryFetchResponse.listingType;
        const totalFound = categoryFetchResponse.totalFound;
        const name = categoryFetchResponse.categoryName;
        const datalayerParams = {
          ...params,
          sort: categoryFetchResponse.sort,
          filters: categoryFetchResponse.filters,
        };
        const saleTrackingData = getPRListAndPRCount(
          categoryFetchResponse.products
        );
        setDataLayer({
          id,
          level: 0,
          listingType,
          params: datalayerParams,
          pathname,
          totalFound,
          name,
          analyticsSdkData: {
            plpDnHybridPageType: dnHybridPageType,
            plpCategoryId: id,
            plpCategoryName: name,
          },
          saleTrackingData,
        });
      } catch (error) {
        logger.error(error, 'Error in CategoryListing Promise.allSettled');
      }
    }
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(frontloadConnect(fetchData)(View));
